<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { UiRichTextEditor, UiPillSelect, UiButton, UiText, UiIcon, UiTooltip, useToaster } from "@shopexperts/shared";
import type { UiPillSelectOption } from "@shopexperts/shared";
import { captureConvexError } from "@/v2Projects/telemetry";
import { buildProjectUserError } from "@/v2Projects/userErrors";
import { ATTACHMENT_ACCEPT, checkAttachment, describeUploadFailure, type AttachmentFailure } from "../attachmentRules";
import type { ProjectSide, ChatMessageAttachment, NoteKind } from "@/v2Projects/types";
import type { ProjectMediaResource } from "@shopexperts/legacy-api";
import ChatComposerSettingsDialog from "./ChatComposerSettingsDialog.vue";
import { getInitialSendMessageKeymap, type SendMessageKeymap } from "./chatComposerSettings";

const props = defineProps<{
  compact?: boolean;
  initialMessage?: string;
  projectId?: number;
  side: ProjectSide;
  isAdmin?: boolean;
  actingOrgId: number;
  threadId: string | null;
  disabled?: boolean;
  sendMessage: (input: {
    threadId: string;
    content: string;
    attachments: ChatMessageAttachment[];
  }) => Promise<unknown>;
  sendNote: (input: {
    threadId: string;
    noteKind: NoteKind;
    content: string;
    attachments: ChatMessageAttachment[];
  }) => Promise<unknown>;
  uploadAttachments: (input: {
    files: File[];
    actingOrgId: number;
  }) => Promise<ProjectMediaResource[]>;
}>();

const emit = defineEmits<{ sent: []; draftChange: [html: string] }>();
const toaster = useToaster();

const messageHtml = ref(props.initialMessage ?? "");
watch(messageHtml, html => emit("draftChange", html), { flush: "sync" });
const selectedType = ref("message");
const isSending = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);
const isExpanded = ref(false);
const isSettingsOpen = ref(false);

interface FileUploadState {
  id: string;
  file: File;
  status: "uploading" | "uploaded" | "failed";
  result?: ProjectMediaResource;
  failure?: AttachmentFailure;
  previewUrl?: string;
}

const pendingFiles = ref<FileUploadState[]>([]);

const sendMessageKeymap = ref<SendMessageKeymap>("enter");

const typeOptions = computed<UiPillSelectOption[]>(() => {
  if (props.isAdmin) {
    return [
      { value: "message", label: "Message", description: props.projectId ? "Visible to everyone on this project" : "Visible to everyone in this conversation" },
      { value: "admin_note", label: "Admin Note", description: "Visible only to admins" },
    ];
  }

  if (props.side === "expert") {
    return [
      { value: "message", label: "Message", description: props.projectId ? "Visible to everyone on this project" : "Visible to everyone in this conversation" },
      { value: "expert_note", label: "Expert Note", description: props.projectId ? "Visible to all experts on this project" : "Visible to the expert organization" },
      { value: "personal_note", label: "Personal Note", description: "Only you can see this note" },
    ];
  }
  return [
    { value: "message", label: "Message", description: props.projectId ? "Visible to everyone on this project" : "Visible to everyone in this conversation" },
    { value: "client_note", label: "Client Note", description: props.projectId ? "Visible to all clients on this project" : "Visible to the client organization" },
    { value: "personal_note", label: "Personal Note", description: "Only you can see this note" },
  ];
});

const noteInfoText = computed(() => {
  switch (selectedType.value) {
    case "admin_note":
      return "Visible only to admins";
    case "personal_note":
      return "Only you can see this note";
    case "expert_note":
      return props.projectId ? "Visible to all experts on this project" : "Visible to the expert organization";
    case "client_note":
      return props.projectId ? "Visible to all clients on this project" : "Visible to the client organization";
    default:
      return null;
  }
});

const composerBgClass = computed(() => {
  if (selectedType.value === "admin_note") return "bg-sky-50";
  if (selectedType.value === "personal_note") return "bg-violet-50";
  if (selectedType.value === "expert_note" || selectedType.value === "client_note") return "bg-amber-50";
  return "bg-white";
});

const pillStyle = computed(() => {
  switch (selectedType.value) {
    case "admin_note":
      return {
        bg: "bg-sky-100 hover:bg-sky-200/80",
        text: "text-sky-700",
        icon: "text-sky-700",
      };
    case "personal_note":
      return {
        bg: "bg-violet-100 hover:bg-violet-200/80",
        text: "text-violet-700",
        icon: "text-violet-700",
      };
    case "expert_note":
    case "client_note":
      return {
        bg: "bg-amber-100 hover:bg-amber-200/80",
        text: "text-amber-700",
        icon: "text-amber-700",
      };
    default:
      return {
        bg: "bg-se-gray-100 hover:bg-se-gray-200",
        text: "text-se-dark",
        icon: "text-se-gray-700",
      };
  }
});

const placeholderText = computed(() => {
  switch (selectedType.value) {
    case "admin_note":
      return "Write an admin note...";
    case "personal_note":
      return "Write a personal note...";
    case "expert_note":
      return "Write a note for experts...";
    case "client_note":
      return "Write a note for clients...";
    default:
      return "Type a message...";
  }
});

const editorRows = computed(() => (isExpanded.value ? undefined : props.compact ? 2 : 3));

function stripHtml(input: string): string {
  return input
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const hasContent = computed(() => stripHtml(messageHtml.value).length > 0);
const hasUploadedFiles = computed(
  () => pendingFiles.value.some((f) => f.status === "uploaded"),
);
const hasUploading = computed(
  () => pendingFiles.value.some((f) => f.status === "uploading"),
);
const canSubmit = computed(
  () =>
    (hasContent.value || hasUploadedFiles.value) &&
    !hasUploading.value &&
    !isSending.value &&
    !!props.threadId,
);

onMounted(() => {
  sendMessageKeymap.value = getInitialSendMessageKeymap();
});

function openFilePicker() {
  fileInput.value?.click();
}

function openComposerSettings() {
  isSettingsOpen.value = true;
}

async function uploadOne(state: FileUploadState) {
  const setState = (patch: Partial<FileUploadState>) => {
    const idx = pendingFiles.value.findIndex((f) => f.id === state.id);
    if (idx !== -1) pendingFiles.value[idx] = { ...pendingFiles.value[idx]!, ...patch };
  };
  try {
    const [result] = await props.uploadAttachments({ files: [state.file], actingOrgId: props.actingOrgId });
    if (!result) throw new Error("The upload returned no file.");
    setState({ status: "uploaded", result, failure: undefined });
    return null;
  } catch (error) {
    const failure = describeUploadFailure(state.file, error);
    if (failure.problem === "failed" || failure.problem === "network") {
      captureConvexError(error, {
        operation: "chat.composer_upload_failed",
        projectId: props.projectId,
        threadId: props.threadId,
        side: props.side,
        counts: { files: 1 },
        extra: { actingOrgId: props.actingOrgId, size: state.file.size },
      });
    }
    setState({ status: "failed", failure });
    return failure;
  }
}

function reportFailures(failures: AttachmentFailure[]) {
  if (failures.length === 0) return;
  toaster.create({
    title: failures.length === 1 ? "A file wasn't attached" : `${failures.length} files weren't attached`,
    description: failures.map((failure) => failure.reason).join(" "),
    type: "error",
  });
}

async function handleFilesSelected(event: Event) {
  const input = event.target as HTMLInputElement;
  const files = Array.from(input.files ?? []);
  input.value = "";
  if (files.length === 0) return;

  const newStates: FileUploadState[] = files.map((file) => {
    const failure = checkAttachment(file) ?? undefined;
    return {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      status: failure ? "failed" as const : "uploading" as const,
      failure,
      previewUrl: !failure && file.type.startsWith("image/") ? URL.createObjectURL(file) : undefined,
    };
  });
  pendingFiles.value = [...pendingFiles.value, ...newStates];

  const rejected = newStates.flatMap((state) => (state.failure ? [state.failure] : []));
  const uploaded = await Promise.all(newStates.filter((state) => !state.failure).map(uploadOne));
  reportFailures([...rejected, ...uploaded.filter((failure): failure is AttachmentFailure => Boolean(failure))]);
}

async function retryFile(id: string) {
  const state = pendingFiles.value.find((f) => f.id === id);
  if (!state || state.failure?.problem === "too_large" || state.failure?.problem === "unsupported") return;
  const idx = pendingFiles.value.indexOf(state);
  pendingFiles.value[idx] = { ...state, status: "uploading", failure: undefined };
  reportFailures([await uploadOne(pendingFiles.value[idx]!)].filter((failure): failure is AttachmentFailure => Boolean(failure)));
}

function removeFile(id: string) {
  const file = pendingFiles.value.find((f) => f.id === id);
  if (file?.previewUrl) URL.revokeObjectURL(file.previewUrl);
  pendingFiles.value = pendingFiles.value.filter((f) => f.id !== id);
}

function cycleSelectedType(step: number) {
  const options = typeOptions.value;
  if (options.length === 0) return;

  const currentIndex = options.findIndex((option) => option.value === selectedType.value);
  const startIndex = currentIndex === -1 ? 0 : currentIndex;
  const nextIndex = (startIndex + step + options.length) % options.length;
  selectedType.value = options[nextIndex]!.value;
}

function handleEditorKeydown(event: KeyboardEvent) {
  const target = event.target as HTMLElement | null;
  const isEditorTarget = !!target?.closest(".ProseMirror");
  if (!isEditorTarget) return;
  if (event.isComposing) return;
  if (event.key === "Tab" && event.shiftKey) {
    event.preventDefault();
    cycleSelectedType(-1);
    return;
  }
  if (event.key !== "Enter") return;
  const shouldSend = (() => {
    switch (sendMessageKeymap.value) {
      case "enter":
        return !event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey;
      case "shift-enter":
        return event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey;
      case "ctrl-enter":
        return !event.shiftKey && !event.altKey && (event.ctrlKey || event.metaKey);
      case "none":
        return false;
    }
  })();
  if (!shouldSend) return;

  event.preventDefault();
  void submit();
}

function buildAttachments(): ChatMessageAttachment[] {
  return pendingFiles.value
    .filter((f) => f.status === "uploaded" && f.result)
    .map((f) => ({
      media_id: f.result!.id,
      type: "file" as const,
      name: f.result!.filename,
      url: f.result!.cdn_url ?? f.result!.url ?? undefined,
      mime_type: f.result!.mime_type ?? undefined,
    }));
}

async function submit() {
  if (!canSubmit.value || !props.threadId) return;
  isSending.value = true;

  try {
    const content = messageHtml.value.trim();
    const attachments = buildAttachments();

    if (selectedType.value === "message") {
      await props.sendMessage({
        threadId: props.threadId,
        content,
        attachments,
      });
    } else {
      const noteKind: NoteKind =
        selectedType.value === "admin_note"
          ? "admin_note"
          : selectedType.value === "personal_note"
            ? "personal_note"
            : "group_note";
      await props.sendNote({
        threadId: props.threadId,
        noteKind,
        content,
        attachments,
      });
    }

    messageHtml.value = "";
    pendingFiles.value.forEach((f) => {
      if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
    });
    pendingFiles.value = [];
    selectedType.value = "message";
    isExpanded.value = false;
    emit("sent");
  } catch (error) {
    const action = selectedType.value === "message" ? "sendMessage" : "sendNote";
    const supportReference = captureConvexError(error, {
      operation: selectedType.value === "message"
        ? "chat.composer_send_message_failed"
        : "chat.composer_send_note_failed",
      projectId: props.projectId,
      threadId: props.threadId,
      side: props.side,
      counts: { attachments: buildAttachments().length },
      extra: { selectedType: selectedType.value },
    });
    const message = buildProjectUserError(action, error, supportReference);
    toaster.create({
      title: message.title,
      description: message.description,
      type: "error",
    });
  } finally {
    isSending.value = false;
  }
}

</script>

<template>
  <div
    class="transition-colors duration-200"
    :class="[
      composerBgClass,
      isExpanded
        ? 'absolute inset-0 z-20 flex min-h-0 flex-col overflow-hidden rounded-xl border border-gray-200 max-sm:rounded-none'
        : 'border-t border-gray-200',
    ]"
  >
    <!-- Attachment Previews -->
    <div
      v-if="pendingFiles.length > 0"
      class="flex gap-2 px-3.5 pt-3 pb-1 overflow-x-auto"
    >
      <div
        v-for="file in pendingFiles"
        :key="file.id"
        class="relative shrink-0 w-20 h-16 rounded-lg border border-gray-200 overflow-hidden bg-gray-50 group"
      >
        <!-- Image preview -->
        <img
          v-if="file.previewUrl"
          :src="file.previewUrl"
          :alt="file.file.name"
          class="w-full h-full object-cover"
        />
        <!-- File preview -->
        <div v-else class="w-full h-full flex flex-col items-center justify-center px-1.5 gap-0.5">
          <svg class="w-5 h-5 text-gray-400" viewBox="0 0 16 16" fill="currentColor">
            <path d="M4 1a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V5.414a1 1 0 0 0-.293-.707L10.293 1.293A1 1 0 0 0 9.586 1H4zm5 1.5v2A1.5 1.5 0 0 0 10.5 6H13v7a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1h5v.5z" />
          </svg>
          <span class="text-[9px] text-gray-500 truncate w-full text-center leading-tight">
            {{ file.file.name }}
          </span>
        </div>

        <!-- Upload overlay -->
        <div
          v-if="file.status === 'uploading'"
          class="absolute inset-0 bg-white/60 flex items-center justify-center"
        >
          <div class="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
        </div>

        <!-- Failed overlay -->
        <div
          v-if="file.status === 'failed'"
          class="absolute inset-0 flex flex-col items-center justify-center gap-0.5 bg-red-50/90 px-1 text-center"
          role="alert"
          :title="file.failure?.reason"
          :aria-label="file.failure?.reason ?? `${file.file.name} failed to upload`"
        >
          <span class="text-[10px] font-medium leading-tight text-red-600">{{ file.failure?.label ?? "Failed" }}</span>
          <span class="w-full truncate text-[9px] leading-tight text-red-500">{{ file.file.name }}</span>
          <button
            v-if="file.failure?.problem !== 'too_large' && file.failure?.problem !== 'unsupported'"
            type="button"
            class="text-[10px] font-medium leading-tight text-se-dark underline"
            @click="retryFile(file.id)"
          >
            Retry
          </button>
        </div>

        <!-- Remove button -->
        <button
          class="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-gray-800/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          @click="removeFile(file.id)"
        >
          <svg class="w-2.5 h-2.5" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M2 2l8 8M10 2l-8 8" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Editor -->
    <div
      class="px-3.5 pt-2 pb-1 composer-editor"
      :class="isExpanded ? 'flex min-h-0 flex-1' : ''"
      @keydown.capture="handleEditorKeydown"
    >
      <UiRichTextEditor
        v-model="messageHtml"
        :rows="editorRows"
        :fill="isExpanded"
        :placeholder="placeholderText"
        :disabled="disabled || !threadId"
      >
        <template #toolbar-extra>
          <UiTooltip content="Composer settings">
            <button
              type="button"
              class="inline-flex h-7 w-7 items-center justify-center rounded-sm text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
              aria-label="Composer settings"
              @click.stop="openComposerSettings"
            >
              <UiIcon name="settings-01" class="h-4 w-4" />
            </button>
          </UiTooltip>
          <UiTooltip :content="isExpanded ? 'Collapse composer' : 'Expand composer'">
            <button
              type="button"
              class="inline-flex h-7 w-7 items-center justify-center rounded-sm text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
              :aria-label="isExpanded ? 'Collapse composer' : 'Expand composer'"
              @click.stop="isExpanded = !isExpanded"
            >
              <UiIcon :name="isExpanded ? 'minimize-01' : 'expand-01'" class="h-4 w-4" />
            </button>
          </UiTooltip>
        </template>
      </UiRichTextEditor>
    </div>

    <div class="flex flex-wrap items-center gap-x-2 gap-y-1 px-3.5 py-2 max-sm:flex-nowrap">
      <button
        type="button"
        class="inline-flex shrink-0 items-center justify-center min-h-8 min-w-8 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
        :disabled="disabled || !threadId"
        @click="openFilePicker"
      >
        <svg class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M15.621 4.379a3 3 0 00-4.242 0l-7 7a3 3 0 004.241 4.243h.001l.497-.5a.75.75 0 011.064 1.057l-.498.501-.002.002a4.5 4.5 0 01-6.364-6.364l7-7a4.5 4.5 0 016.368 6.36l-3.455 3.553A2.625 2.625 0 119.52 9.52l3.45-3.451a.75.75 0 111.061 1.06l-3.45 3.451a1.125 1.125 0 001.587 1.595l3.454-3.553a3 3 0 000-4.242z" clip-rule="evenodd" />
        </svg>
      </button>

      <input
        ref="fileInput"
        type="file"
        multiple
        :accept="ATTACHMENT_ACCEPT"
        class="hidden"
        @change="handleFilesSelected"
      />

      <UiPillSelect
        v-model="selectedType"
        :options="typeOptions"
        size="lg"
        embedded
        :bg-class="pillStyle.bg"
        :text-class="pillStyle.text"
        :icon-class="pillStyle.icon"
        :aria-label="'Message type'"
      />

      <UiText
        v-if="noteInfoText"
        as="span"
        variant="p-small"
        class="order-first basis-full pl-9 text-[11px] leading-tight sm:order-0 sm:basis-auto sm:min-w-0 sm:flex-1 sm:pl-0 max-sm:hidden"
        :class="selectedType === 'admin_note' ? 'text-sky-600' : selectedType === 'personal_note' ? 'text-violet-500' : 'text-amber-600'"
      >
        {{ noteInfoText }}
      </UiText>

      <UiButton
        size="sm"
        :variant="canSubmit ? 'primary' : 'ghost'"
        label="Send"
        :disabled="!canSubmit"
        class="ml-auto shrink-0 max-sm:hidden"
        @click="submit"
      >
        <template #after_label>
          <UiIcon name="arrow-send" class="w-4 h-4" />
        </template>
      </UiButton>

      <!-- `max-sm:contents` wrapper avoids a Tailwind v4 cascade collision with the button's static `inline-flex`. -->
      <div class="hidden max-sm:contents">
        <UiButton
          size="sm"
          icon="arrow-send"
          :variant="canSubmit ? 'primary' : 'ghost'"
          :disabled="!canSubmit"
          aria-label="Send message"
          class="shrink-0 max-sm:ml-auto"
          @click="submit"
        />
      </div>
    </div>

    <ChatComposerSettingsDialog v-model:open="isSettingsOpen" v-model:keymap="sendMessageKeymap" />
  </div>
</template>

<style scoped>
/* Strip `UiFieldContainer` chrome so the editor inherits the composer's tinted background. */
.composer-editor :deep(.rounded-lg.border) {
  border: none;
  border-radius: 0;
  background-color: transparent;
}
.composer-editor :deep(.border-b-1) {
  border-color: transparent;
}

/* Tighten `UiFieldContainer`'s `px-4` so editor text aligns with the toolbar/action-bar gutter. */
.composer-editor :deep(.rounded-lg) {
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

/* Pull the formatting toolbar's first icon flush so its glyph aligns with the placeholder/attachment glyphs. */
.composer-editor :deep(.absolute.top-0.left-0.h-10) {
  padding-left: 0;
}
.composer-editor :deep(.absolute.top-0.left-0.h-10) > * {
  margin-left: -0.025rem;
}

</style>
